import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildPostAnalyticsApiPath,
  buildPostAnalyticsTopicIdsQuery,
  normalizePostAnalyticsResponse,
} from "../../lib/utils/postAnalytics"

export const fetchLiveEngagement = createAsyncThunk(
  "liveEngagement/fetchLiveEngagement",
  async ({ clientId, topicIds }, { getState, rejectWithValue }) => {
    try {
      const apiPath = buildPostAnalyticsApiPath(clientId)
      if (!apiPath) {
        return rejectWithValue("Client ID is required")
      }

      if (!Array.isArray(topicIds) || topicIds.length === 0) {
        return { response: [], topicIds: [] }
      }

      const state = getState()
      const alreadyFetched = state.liveEngagement.fetchedTopicIds || []

      const newTopicIds = topicIds.filter((id) => !alreadyFetched.includes(id))

      if (newTopicIds.length === 0) {
        return { response: [], topicIds: [] }
      }

      const query = buildPostAnalyticsTopicIdsQuery(newTopicIds)
      const response = await get(`${apiPath}?${query}`)

      return {
        response,
        topicIds: newTopicIds,
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch engagement")
    }
  },
)

const liveEngagementSlice = createSlice({
  name: "liveEngagement",

  initialState: {
    list: [],
    map: {},
    fetchedTopicIds: [],
    fetchStatus: "idle",
    fetchError: null,
  },

  reducers: {
    clearLiveEngagement(state) {
      state.list = []
      state.map = {}
      state.fetchedTopicIds = []
      state.fetchStatus = "idle"
      state.fetchError = null
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchLiveEngagement.pending, (state, action) => {
        if (action.meta.arg?.topicIds?.length > 0) {
          state.fetchStatus = "loading"
        }
      })

      .addCase(fetchLiveEngagement.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded"

        const normalized = normalizePostAnalyticsResponse(action.payload.response)

        normalized.forEach((analyticsItem) => {
          state.map[analyticsItem.postId] = analyticsItem

          const listIdx = state.list.findIndex((x) => x.postId === analyticsItem.postId)
          if (listIdx >= 0) {
            state.list[listIdx] = analyticsItem
          } else {
            state.list.push(analyticsItem)
          }
        })

        action.payload.topicIds.forEach((id) => {
          if (!state.fetchedTopicIds.includes(id)) {
            state.fetchedTopicIds.push(id)
          }
        })
      })

      .addCase(fetchLiveEngagement.rejected, (state, action) => {
        state.fetchStatus = "failed"
        state.fetchError = action.payload
      })
  },
})

export const { clearLiveEngagement } = liveEngagementSlice.actions

export default liveEngagementSlice.reducer
